import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, type Column } from "@legal-500/legal500-design-system";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import LoadingState from "../../../components/LoadingState/index.js";
import EmptyState from "../../../components/EmptyState/index.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { useQuery } from "../../../hooks/useApi.js";
import {
  developerUsersService,
  type VerifiedUser,
  type NormalizeSubmissionsResponse,
} from "../../../api/developerUsers.service.js";
import styles from "./Users.module.css";
import { StatusBadge, isValidFirmRef, type NormalizeStatus } from "./shared.js";

type UserRow = VerifiedUser & {
  normalizeStatus: NormalizeStatus;
  result?: NormalizeSubmissionsResponse;
  error?: string;
};

function VerifiedUsersTab() {
  const { data, loading, error, refetch } = useQuery(
    () => developerUsersService.getVerifiedUsersWithFirms(),
    [],
  );

  const [userRows, setUserRows] = useState<UserRow[]>([]);
  const [normalizeAllLoading, setNormalizeAllLoading] = useState(false);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    if (data?.users) {
      setUserRows(
        data.users.map(user => ({
          ...user,
          normalizeStatus: "pending" as NormalizeStatus,
        })),
      );
    }
  }, [data]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleNormalize = useCallback(async (user: UserRow) => {
    if (!isValidFirmRef(user.firmRef)) return;

    setUserRows(prev =>
      prev.map(u =>
        u.externalId === user.externalId
          ? { ...u, normalizeStatus: "loading" as NormalizeStatus }
          : u,
      ),
    );

    try {
      const result = await developerUsersService.normalizeSubmissions({
        userId: user.externalId,
        firmRef: user.firmRef,
        firmName: user.firmName,
      });

      setUserRows(prev =>
        prev.map(u =>
          u.externalId === user.externalId
            ? { ...u, normalizeStatus: "success" as NormalizeStatus, result }
            : u,
        ),
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to normalize";
      setUserRows(prev =>
        prev.map(u =>
          u.externalId === user.externalId
            ? { ...u, normalizeStatus: "failed" as NormalizeStatus, error: errorMessage }
            : u,
        ),
      );
    }
  }, []);

  const handleNormalizeAll = useCallback(async () => {
    const pendingUsers = userRows.filter(
      u => u.normalizeStatus === "pending" && isValidFirmRef(u.firmRef),
    );
    if (pendingUsers.length === 0) return;

    if (!window.confirm(`Normalize submissions for ${pendingUsers.length} user(s)?`)) return;

    setNormalizeAllLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const user of pendingUsers) {
      try {
        setUserRows(prev =>
          prev.map(u =>
            u.externalId === user.externalId
              ? { ...u, normalizeStatus: "loading" as NormalizeStatus }
              : u,
          ),
        );

        const result = await developerUsersService.normalizeSubmissions({
          userId: user.externalId,
          firmRef: user.firmRef,
          firmName: user.firmName,
        });

        setUserRows(prev =>
          prev.map(u =>
            u.externalId === user.externalId
              ? { ...u, normalizeStatus: "success" as NormalizeStatus, result }
              : u,
          ),
        );
        successCount++;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to normalize";
        setUserRows(prev =>
          prev.map(u =>
            u.externalId === user.externalId
              ? { ...u, normalizeStatus: "failed" as NormalizeStatus, error: errorMessage }
              : u,
          ),
        );
        failCount++;
      }
    }

    setNormalizeAllLoading(false);
    setNotification({
      type: failCount === 0 ? "success" : "error",
      message:
        failCount === 0
          ? `Successfully normalized ${successCount} user(s)`
          : `Completed: ${successCount} succeeded, ${failCount} failed`,
    });
  }, [userRows]);

  const pendingCount = userRows.filter(
    u => u.normalizeStatus === "pending" && isValidFirmRef(u.firmRef),
  ).length;

  const columns: Column<UserRow>[] = useMemo(
    () => [
      {
        header: "External ID",
        accessor: "externalId" as keyof UserRow,
        render: (value: unknown) => String(value),
      },
      {
        header: "Firm Ref",
        accessor: "firmRef" as keyof UserRow,
        render: (value: unknown) => String(value),
      },
      {
        header: "Firm Name",
        accessor: "firmName" as keyof UserRow,
        render: (value: unknown) => String(value),
      },
      {
        header: "Status",
        accessor: "normalizeStatus" as keyof UserRow,
        render: (value: unknown, row: UserRow) => {
          const status = value as NormalizeStatus;
          return (
            <div>
              <StatusBadge status={status} />
              {status === "success" && row.result && (
                <div className={styles.resultDetails}>
                  {row.result.updatedSubmissions} submissions, {row.result.updatedDocumentFiles}{" "}
                  docs, {row.result.updatedRefereeFiles} referees
                </div>
              )}
              {status === "failed" && row.error && (
                <div className={styles.resultDetails}>{row.error}</div>
              )}
            </div>
          );
        },
      },
      {
        header: "Actions",
        accessor: "externalId" as keyof UserRow,
        render: (_value: unknown, row: UserRow) => {
          if (row.normalizeStatus === "loading") {
            return (
              <Button variant="outlined" color="primary" size="small" disabled>
                Normalizing...
              </Button>
            );
          }

          if (row.normalizeStatus === "success") {
            return null;
          }

          return (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled={normalizeAllLoading || !isValidFirmRef(row.firmRef)}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                handleNormalize(row);
              }}
            >
              Normalize
            </Button>
          );
        },
      },
    ],
    [handleNormalize, normalizeAllLoading],
  );

  const status = (error as Error & { status?: number })?.status;
  const isAuthError = status === 401 || status === 403;

  if (isAuthError) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.content}>
      {notification && (
        <div className={`${styles.notification} ${styles[notification.type]}`}>
          {notification.message}
          <button
            type="button"
            onClick={() => setNotification(null)}
            className={styles.notificationClose}
            aria-label="Dismiss notification"
          >
            x
          </button>
        </div>
      )}

      {loading && <LoadingState message="Loading verified users..." />}

      {error && !isAuthError && (
        <EmptyState
          variant="error"
          title="Failed to load users"
          message={error.message}
          action={{
            label: "Retry",
            onClick: refetch,
          }}
        />
      )}

      {!loading && !error && userRows.length === 0 && (
        <EmptyState
          variant="empty"
          title="No verified users found"
          message="There are no verified users with firm associations."
        />
      )}

      {!loading && !error && userRows.length > 0 && (
        <>
          <div className={styles.toolbar}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNormalizeAll}
              disabled={normalizeAllLoading || pendingCount === 0}
            >
              {normalizeAllLoading ? "Normalizing..." : `Normalize All (${pendingCount})`}
            </Button>
          </div>

          <div className={styles.tableContainer}>
            <DataTable data={userRows} columns={columns} rowKey="externalId" size="small" />
          </div>
        </>
      )}
    </div>
  );
}

export default VerifiedUsersTab;
